import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eatDayOf, formatIsoDay, INCIDENT_TEXT_LIMITS } from '@wodi/domain';
import type { IncidentActionView, IncidentView } from '@wodi/shared';
import { Button, DateField, Notice, Section, Select, TextArea, TextField } from '@wodi/ui';
import { useState } from 'react';

import { incidentKeys, incidentPeopleQuery, incidentSteps } from '../api/incidents.ts';
import { dayText, errorText } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { TextDialog } from '../operations/text-dialog.tsx';
import { toasts } from '../toasts.ts';
import { ActionStatusTag } from './words.tsx';

// The safety lead's review (docs/plans/phase3-operations-modules.md §1.1): start the review
// with a reviewer, record the root cause, add actions with owners and due dates, finish
// them, close, reopen or enter in error. Each step answers with the incident as it now
// reads, so the page never shows a stale status.

const L = INCIDENT_TEXT_LIMITS;

function useStep(incident: IncidentView) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (run: () => Promise<IncidentView>) => run(),
    onSuccess: (view) => {
      queryClient.setQueryData(incidentKeys.one(incident.id), view);
      void queryClient.invalidateQueries({ queryKey: ['incidents', 'list'] });
    },
  });
}

function peopleOptions(items: readonly { id: string; displayName: string }[] | undefined) {
  return (items ?? []).map((p) => ({ id: p.id, label: p.displayName }));
}

function StartReview({ incident, online }: { incident: IncidentView; online: boolean }) {
  const people = useQuery(incidentPeopleQuery);
  const [reviewer, setReviewer] = useState<string | null>(null);
  const step = useStep(incident);
  return (
    <form
      className={styles.form}
      aria-label="Start the review"
      onSubmit={(event) => {
        event.preventDefault();
        if (reviewer === null) return;
        step.mutate(() => incidentSteps.startReview(incident.id, reviewer), {
          onSuccess: () => {
            toasts.show({ title: 'Review started' });
          },
        });
      }}
    >
      <Select
        label="Reviewer"
        placeholder="Choose who reviews it"
        options={peopleOptions(people.data?.items)}
        value={reviewer}
        onChange={(key) => {
          setReviewer(key === null ? null : String(key));
        }}
        width="lg"
      />
      {step.isError ? <Notice tone="error">{errorText(step.error, 'Try again.')}</Notice> : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || reviewer === null}
          isPending={step.isPending}
          pendingLabel="Starting…"
        >
          Start review
        </Button>
      </div>
    </form>
  );
}

function RootCause({ incident, online }: { incident: IncidentView; online: boolean }) {
  const [text, setText] = useState(incident.rootCause ?? '');
  const step = useStep(incident);
  return (
    <form
      className={styles.form}
      aria-label="Root cause"
      onSubmit={(event) => {
        event.preventDefault();
        if (text.trim() === '') return;
        step.mutate(() => incidentSteps.rootCause(incident.id, text.trim()), {
          onSuccess: () => {
            toasts.show({ title: 'Root cause saved' });
          },
        });
      }}
    >
      <TextArea
        label="Root cause"
        description="Why it happened: the system, not the person."
        value={text}
        onChange={setText}
        rows={4}
        maxLength={L.rootCause}
      />
      {step.isError ? <Notice tone="error">{errorText(step.error, 'Try again.')}</Notice> : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || text.trim() === ''}
          isPending={step.isPending}
          pendingLabel="Saving…"
        >
          Save root cause
        </Button>
      </div>
    </form>
  );
}

function AddAction({ incident, online }: { incident: IncidentView; online: boolean }) {
  const people = useQuery(incidentPeopleQuery);
  const today = formatIsoDay(eatDayOf(new Date()));
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState<string | null>(null);
  const [due, setDue] = useState<string | null>(null);
  const step = useStep(incident);
  const ready = description.trim() !== '' && owner !== null && due !== null;
  return (
    <form
      className={styles.form}
      aria-label="Add an action"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        step.mutate(
          () =>
            incidentSteps.addAction(incident.id, {
              description: description.trim(),
              ownerId: owner,
              dueOn: due,
            }),
          {
            onSuccess: () => {
              setDescription('');
              setOwner(null);
              setDue(null);
              toasts.show({ title: 'Action added' });
            },
          },
        );
      }}
    >
      <TextField
        label="Action"
        description="What will be done to stop it happening again."
        value={description}
        onChange={setDescription}
        maxLength={L.actionDescription}
      />
      <div className={styles.fields}>
        <Select
          label="Owner"
          placeholder="Choose who does it"
          options={peopleOptions(people.data?.items)}
          value={owner}
          onChange={(key) => {
            setOwner(key === null ? null : String(key));
          }}
        />
        <DateField label="Due" value={due} onChange={setDue} minValue={today} />
      </div>
      {step.isError ? <Notice tone="error">{errorText(step.error, 'Try again.')}</Notice> : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || !ready}
          isPending={step.isPending}
          pendingLabel="Adding…"
        >
          Add action
        </Button>
      </div>
    </form>
  );
}

type Pending =
  | { kind: 'done'; action: IncidentActionView }
  | { kind: 'cancel'; action: IncidentActionView }
  | { kind: 'close' }
  | { kind: 'reopen' }
  | { kind: 'error' };

const DIALOGS = {
  done: {
    title: 'Mark the action done',
    label: 'What was done',
    confirm: 'Mark done',
    pending: 'Saving…',
    toast: 'Action done',
    min: 1,
  },
  cancel: {
    title: 'Cancel the action',
    label: 'Reason',
    confirm: 'Cancel action',
    pending: 'Cancelling…',
    toast: 'Action cancelled',
    min: 5,
  },
  close: {
    title: 'Close the incident',
    label: 'Closing note',
    confirm: 'Close incident',
    pending: 'Closing…',
    toast: 'Incident closed',
    min: 1,
  },
  reopen: {
    title: 'Reopen the incident',
    label: 'Reason',
    confirm: 'Reopen incident',
    pending: 'Reopening…',
    toast: 'Incident reopened',
    min: 5,
  },
  error: {
    title: 'Mark entered in error',
    label: 'Reason',
    confirm: 'Mark entered in error',
    pending: 'Saving…',
    toast: 'Marked entered in error',
    min: 5,
  },
} as const;

export function ActionsList({
  incident,
  onFinish,
  online,
}: {
  incident: IncidentView;
  onFinish?: (pending: Pending) => void;
  online: boolean;
}) {
  if (incident.actions.length === 0)
    return (
      <p className={styles.meta}>No actions yet. The reviewer adds them after the root cause.</p>
    );
  return (
    <ul className={styles.list} aria-label="Actions">
      {incident.actions.map((a) => (
        <li key={a.id} className={styles.item}>
          <div className={styles.itemMain}>
            <span>{a.description}</span>
            <span className={styles.meta}>
              {a.owner.displayName}, due {dayText(a.dueOn)}
              {a.outcomeNote === null ? '' : `. ${a.outcomeNote}`}
            </span>
          </div>
          <div className={styles.actions}>
            <ActionStatusTag status={a.status} overdue={a.overdue} />
            {onFinish !== undefined && a.status === 'open' ? (
              <>
                <Button
                  dense
                  isDisabled={!online}
                  onPress={() => {
                    onFinish({ kind: 'done', action: a });
                  }}
                >
                  Mark done
                </Button>
                <Button
                  dense
                  variant="quiet"
                  isDisabled={!online}
                  onPress={() => {
                    onFinish({ kind: 'cancel', action: a });
                  }}
                >
                  Cancel action
                </Button>
              </>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ReviewPanel({ incident, online }: { incident: IncidentView; online: boolean }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const step = useStep(incident);
  const { status } = incident;
  const working = status === 'under_review' || status === 'actions';

  const confirm = (text: string) => {
    if (pending === null) return;
    const id = incident.id;
    const run =
      pending.kind === 'done'
        ? () => incidentSteps.completeAction(id, pending.action.id, text)
        : pending.kind === 'cancel'
          ? () => incidentSteps.cancelAction(id, pending.action.id, text)
          : pending.kind === 'close'
            ? () => incidentSteps.close(id, text)
            : pending.kind === 'reopen'
              ? () => incidentSteps.reopen(id, text)
              : () => incidentSteps.enteredInError(id, text);
    step.mutate(run, {
      onSuccess: () => {
        toasts.show({ title: DIALOGS[pending.kind].toast });
        setPending(null);
      },
    });
  };

  const dialog = pending === null ? null : DIALOGS[pending.kind];
  return (
    <>
      <Section title="Review" description="Only the quality and safety lead's roles see this.">
        <div className={styles.form}>
          {status === 'reported' ? <StartReview incident={incident} online={online} /> : null}
          {working ? (
            <RootCause key={incident.rootCause ?? ''} incident={incident} online={online} />
          ) : null}
          {status === 'closed' && incident.closingNote !== null ? (
            <p className={styles.prose}>Closing note: {incident.closingNote}</p>
          ) : null}
          <div className={styles.actions}>
            {working ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setPending({ kind: 'close' });
                }}
              >
                Close incident
              </Button>
            ) : null}
            {status === 'closed' ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setPending({ kind: 'reopen' });
                }}
              >
                Reopen incident
              </Button>
            ) : null}
            {status === 'reported' || status === 'under_review' ? (
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setPending({ kind: 'error' });
                }}
              >
                Mark entered in error
              </Button>
            ) : null}
          </div>
        </div>
      </Section>
      <Section title="Actions" description="What will be done, by whom and by when.">
        <div className={styles.form}>
          <ActionsList
            incident={incident}
            online={online}
            {...(working ? { onFinish: setPending } : {})}
          />
          {working && incident.rootCause !== null ? (
            <AddAction incident={incident} online={online} />
          ) : null}
        </div>
      </Section>
      {dialog === null ? null : (
        <TextDialog
          title={dialog.title}
          label={dialog.label}
          minLength={dialog.min}
          isOpen
          onOpenChange={(open) => {
            if (!open) {
              setPending(null);
              step.reset();
            }
          }}
          confirmLabel={dialog.confirm}
          pendingLabel={dialog.pending}
          isPending={step.isPending}
          error={step.isError ? errorText(step.error, 'That didn’t work. Try again.') : null}
          onConfirm={confirm}
        />
      )}
    </>
  );
}
